// ============================================================
// CallContext.js  — Global singleton for WebRTC + Transcript
//
// WHY THIS EXISTS:
//   AgentConsole and Noc both mount their OWN WebRTCAgent and
//   WebSocket, so switching views unmounts the active component
//   → WebRTCAgent.cleanup() fires → registerer.unregister() +
//   userAgent.stop() → live call drops.
//
//   This context holds ONE UserAgent, ONE WebSocket, and ONE
//   transcript list for the lifetime of the browser session.
//   AgentConsole and Noc both READ from it; neither owns it.
//
// Place at: /var/www/ai-server-frontend/ai-chat/src/CallContext.js
// ============================================================

import {
  createContext, useContext, useRef, useState,
  useEffect, useCallback,
} from 'react';
import {
  UserAgent, Registerer, Inviter, SessionState,
} from 'sip.js';

// ── SIP constants (same as old WebRTCAgent.js) ───────────────
const SIP_WSS_URL  = `wss://${window.location.hostname}:8089/ws`;
const SIP_DOMAIN   = window.location.hostname;
const SIP_USERNAME = 'agent-webrtc';
const SIP_PASSWORD = 'WorktualWebRTC@2026';
const ICE_SERVERS  = [
  { urls: 'stun:miniai.worktual.tech:3478' },
  { urls: 'turn:miniai.worktual.tech:3478', username: 'worktual', credential: 'TurnPass@2026' },
];

const CallContext = createContext(null);
export const useCall = () => useContext(CallContext);

// ─────────────────────────────────────────────────────────────
export function CallProvider({ token, children }) {
  // ── SIP registration ────────────────────────────────────────
  const [regStatus, setRegStatus]   = useState('disconnected');
  const [error,     setError]       = useState('');

  // ── Call state ──────────────────────────────────────────────
  const [incomingCall, setIncomingCall] = useState(null);
  const [activeCall,   setActiveCall]   = useState(null);
  const [localStream,  setLocalStream]  = useState(null);
  const [remoteStream, setRemoteStream] = useState(null);
  const [callDuration, setCallDuration] = useState(0);
  const [muted,        setMuted]        = useState(false);
  const [onHold,       setOnHold]       = useState(false);
  const [callStatus,   setCallStatus]   = useState('idle'); // idle | ringing | active
  const [recentCalls,  setRecentCalls]  = useState([]);

  // ── Transcription (shared across views) ─────────────────────
  const [liveTranscript, setLiveTranscript] = useState([]);

  // ── Refs that must outlive view switches ────────────────────
  const userAgentRef      = useRef(null);
  const registererRef     = useRef(null);
  const remoteAudioRef    = useRef(null);  // hidden <audio> in provider
  const durationRef       = useRef(null);
  const ringAudioRef      = useRef(null);
  const dtmfAudioCtxRef   = useRef(null);
  const acceptingRef      = useRef(false);
  const wsRef             = useRef(null);
  const mediaRecorderRef  = useRef(null);   // for live STT chunk posting
  const startLiveSttRef   = useRef(null);   // ref to startLiveStt (avoids dep cycle)

  // ── Hidden remote audio element (permanent, not in a view) ──
  useEffect(() => {
    const el = document.createElement('audio');
    el.autoplay = true;
    el.style.display = 'none';
    document.body.appendChild(el);
    remoteAudioRef.current = el;
    return () => { el.remove(); };
  }, []);

  // ── Global WebSocket (transcript + call events) ──────────────
  useEffect(() => {
    if (!token) return;
    const WS_URL = `wss://${window.location.host}/ws`;

    const connect = () => {
      const ws = new WebSocket(`${WS_URL}?token=${token}`);
      wsRef.current = ws;

      ws.onmessage = (e) => {
        try {
          const data = JSON.parse(e.data);
          if (data.type === 'call_transcript_segment') {
            setLiveTranscript(prev =>
              [...prev, { text: data.text, ts: data.ts, speaker: data.speaker || 'unknown' }].slice(-100)
            );
          } else if (data.type === 'call_transcript_ended') {
            setLiveTranscript(prev =>
              [...prev, { text: '--- call ended ---', ts: data.ts, isMarker: true }].slice(-100)
            );
          } else if (data.type === 'call_event') {
            if (data.status === 'ringing_agent') setCallStatus('ringing');
            else if (data.status === 'bridged')   setCallStatus('active');
            else if (data.status === 'ended')     setCallStatus('idle');
          }
        } catch (_) {}
      };

      ws.onclose = () => {
        // Reconnect after 3 s unless token is gone
        if (localStorage.getItem('miniai_jwt')) {
          setTimeout(connect, 3000);
        }
      };

      ws.onerror = () => ws.close();

      const hb = setInterval(() => {
        if (ws.readyState === 1) ws.send(JSON.stringify({ type: 'ping' }));
      }, 25000);

      ws.addEventListener('close', () => clearInterval(hb));
    };

    connect();
    return () => { wsRef.current && wsRef.current.close(); };
  }, [token]);

  // ── Ringtone helpers ─────────────────────────────────────────
  const playRingtone = () => {
    if (!ringAudioRef.current) {
      ringAudioRef.current = new Audio(
        'data:audio/wav;base64,UklGRiQAAABXQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YQAAAAA='
      );
      ringAudioRef.current.loop = true;
    }
    ringAudioRef.current.play().catch(() => {});
  };
  const stopRingtone = () => {
    if (ringAudioRef.current) {
      ringAudioRef.current.pause();
      ringAudioRef.current.currentTime = 0;
    }
  };

  // ── DTMF tone ────────────────────────────────────────────────
  const playDtmfTone = (digit) => {
    const freqs = {
      '1':[697,1209],'2':[697,1336],'3':[697,1477],
      '4':[770,1209],'5':[770,1336],'6':[770,1477],
      '7':[852,1209],'8':[852,1336],'9':[852,1477],
      '*':[941,1209],'0':[941,1336],'#':[941,1477],
    };
    if (!freqs[digit]) return;
    try {
      if (!dtmfAudioCtxRef.current)
        dtmfAudioCtxRef.current = new (window.AudioContext || window.webkitAudioContext)();
      const ctx  = dtmfAudioCtxRef.current;
      const gain = ctx.createGain();
      gain.gain.value = 0.08;
      gain.connect(ctx.destination);
      freqs[digit].forEach(f => {
        const osc = ctx.createOscillator();
        osc.frequency.value = f;
        osc.connect(gain);
        osc.start();
        osc.stop(ctx.currentTime + 0.12);
      });
    } catch (_) {}
  };

  // ── Attach remote audio after call is established ────────────
  const attachRemoteAudio = useCallback((session) => {
    const rStream = new MediaStream();
    const pc = session.sessionDescriptionHandler.peerConnection;
    pc.getReceivers().forEach(r => { if (r.track) rStream.addTrack(r.track); });

    if (remoteAudioRef.current) {
      remoteAudioRef.current.srcObject = rStream;
      remoteAudioRef.current.play().catch(() => {});
    }
    setRemoteStream(rStream);

    const lStream = new MediaStream();
    pc.getSenders().forEach(s => { if (s.track) lStream.addTrack(s.track); });
    setLocalStream(lStream);

    // ── Start live STT: capture both legs, chunk every 3s → Whisper ──
    // STT now handled server-side via Asterisk MixMonitor
    // startLiveSttRef.current(lStream, rStream, session);
  }, []);  // uses ref so no circular dep

  // ── MediaRecorder STT chunker (stored in ref to avoid dep cycle) ─
  // Mixes agent mic (lStream) + caller audio (rStream) into one
  // combined stream, records in 3-second WebM/Opus chunks, posts
  // each chunk to /api/voice/transcript-chunk for Whisper → WS.
  const startLiveStt = useCallback((lStream, rStream, session) => {
    // Stop any previous recorder
    if (mediaRecorderRef.current) {
      try { mediaRecorderRef.current.stop(); } catch (_) {}
      mediaRecorderRef.current = null;
    }

    // Get call_id from session id
    const callId = session.id || ('call_' + Date.now());

    // ── Two separate recorders: one per leg (speaker diarisation) ──
    const recordLeg = (stream, speaker) => {
      if (!stream || stream.getTracks().length === 0) return null;

      let mimeType = 'audio/webm;codecs=opus';
      if (!MediaRecorder.isTypeSupported(mimeType)) mimeType = 'audio/webm';
      if (!MediaRecorder.isTypeSupported(mimeType)) mimeType = '';

      let recorder;
      try {
        recorder = new MediaRecorder(stream, mimeType ? { mimeType } : {});
      } catch (err) {
        console.warn('[STT] MediaRecorder init failed for', speaker, err.message);
        return null;
      }

      // ── KEY FIX: EBML header problem ──────────────────────────
      // MediaRecorder only writes the WebM EBML header in the FIRST
      // ondataavailable chunk. All subsequent chunks are raw WebM
      // clusters with NO header — ffmpeg rejects them as invalid.
      //
      // Solution: keep the first chunk (header) and always prepend
      // it to every subsequent chunk before sending to server.
      // This creates a valid self-contained WebM file every time.
      let headerChunk = null;   // stores the first chunk (EBML header)
      let sending     = false;
      const sendQueue = [];

      const sendNext = async () => {
        if (sending || sendQueue.length === 0) return;
        sending = true;
        const audioData = sendQueue.shift();
        const token = localStorage.getItem('miniai_jwt');
        const form  = new FormData();
        form.append('audio',   audioData, speaker + '_' + Date.now() + '.webm');
        form.append('speaker', speaker);
        form.append('call_id', callId);
        try {
          console.log('[STT] Sending ' + speaker + ' (' + audioData.size + ' bytes)');
          const res  = await fetch('/api/voice/transcript-chunk', {
            method: 'POST',
            headers: token ? { Authorization: 'Bearer ' + token } : {},
            body: form,
          });
          const data = await res.json().catch(() => ({}));
          if (data.text) console.log('[STT] ' + speaker + ': ' + data.text);
        } catch (err) {
          console.warn('[STT] Upload failed:', err.message);
        } finally {
          sending = false;
          sendNext();
        }
      };

      recorder.ondataavailable = (e) => {
        if (!e.data || e.data.size < 500) return;

        if (!headerChunk) {
          // First chunk always contains the EBML header — save it
          headerChunk = e.data;
          console.log('[STT] ' + speaker + ' header chunk saved (' + e.data.size + ' bytes)');
          // Also send the first chunk as-is (it's a complete valid WebM)
          sendQueue.push(e.data);
        } else {
          // All subsequent chunks: prepend the header to make a valid WebM file
          const completeWebM = new Blob([headerChunk, e.data], { type: headerChunk.type });
          console.log('[STT] ' + speaker + ' chunk with header (' + completeWebM.size + ' bytes)');
          sendQueue.push(completeWebM);
        }
        sendNext();
      };

      recorder.onerror = (e) => console.warn('[STT] Recorder error:', e.error);

      try {
        recorder.start(5000);  // 5s chunks
        console.log('[STT] Started', speaker, 'recorder');
      } catch (err) {
        console.warn('[STT] Could not start recorder for', speaker, err.message);
        return null;
      }
      return recorder;
    };

    const agentRecorder  = recordLeg(lStream, 'agent');
    const callerRecorder = recordLeg(rStream, 'caller');

    // Store refs so endCallCleanup can stop them
    mediaRecorderRef.current = { agentRecorder, callerRecorder };
  }, []);

  // Keep ref in sync so attachRemoteAudio can call it without dep cycle
  startLiveSttRef.current = startLiveStt;

  // ── End call cleanup ─────────────────────────────────────────
  const endCallCleanup = useCallback(() => {
    if (durationRef.current) clearInterval(durationRef.current);
    durationRef.current = null;

    // Stop live STT recorders
    if (mediaRecorderRef.current) {
      const { agentRecorder, callerRecorder } = mediaRecorderRef.current;
      try { if (agentRecorder  && agentRecorder.state  !== 'inactive') agentRecorder.stop();  } catch (_) {}
      try { if (callerRecorder && callerRecorder.state !== 'inactive') callerRecorder.stop(); } catch (_) {}
      mediaRecorderRef.current = null;
    }

    setActiveCall(null);
    setCallDuration(0);
    setOnHold(false);
    setMuted(false);
    setCallStatus('idle');
    if (remoteAudioRef.current) remoteAudioRef.current.srcObject = null;
    setRemoteStream(null);
    setLocalStream(null);
  }, []);

  // ── SIP REGISTER ─────────────────────────────────────────────
  const register = useCallback(async () => {
    if (regStatus === 'registered' || regStatus === 'connecting') return;
    if (activeCall) { setError('End the call before re-registering.'); return; }
    setRegStatus('connecting');
    setError('');

    try {
      const uri = UserAgent.makeURI(`sip:${SIP_USERNAME}@${SIP_DOMAIN}`);
      if (!uri) throw new Error('Invalid SIP URI');

      const ua = new UserAgent({
        uri,
        transportOptions:       { server: SIP_WSS_URL },
        authorizationUsername:  SIP_USERNAME,
        authorizationPassword:  SIP_PASSWORD,
        displayName:            'Worktual Agent',
        sessionDescriptionHandlerFactoryOptions: {
          peerConnectionConfiguration: { iceServers: ICE_SERVERS },
        },
        delegate: {
          onInvite: (invitation) => {
            const callerNumber = invitation.remoteIdentity.uri.user || 'Unknown';
            invitation.stateChange.addListener((state) => {
              if (state === SessionState.Terminated) {
                setIncomingCall(prev => (prev?.session === invitation ? null : prev));
                setActiveCall(prev =>   (prev?.session === invitation ? null : prev));
              }
            });
            setIncomingCall({ session: invitation, callerNumber, direction: 'inbound' });
            setCallStatus('ringing');
            playRingtone();
          },
        },
      });

      userAgentRef.current = ua;
      await ua.start();

      const registerer = new Registerer(ua);
      registererRef.current = registerer;
      registerer.stateChange.addListener((state) => {
        if (state === 'Registered')   setRegStatus('registered');
        else if (state === 'Unregistered') setRegStatus('disconnected');
        else if (state === 'Terminated')   setRegStatus('failed');
      });
      await registerer.register();
    } catch (err) {
      console.error('[SIP] Registration error:', err);
      setError(err.message);
      setRegStatus('failed');
    }
  }, [regStatus, activeCall]);

  const unregister = useCallback(async () => {
    try { if (registererRef.current) await registererRef.current.unregister(); } catch (_) {}
    try { if (userAgentRef.current)  await userAgentRef.current.stop();  } catch (_) {}
    setRegStatus('disconnected');
  }, []);

  // ── ACCEPT ───────────────────────────────────────────────────
  const acceptCall = useCallback(async () => {
    if (!incomingCall || acceptingRef.current) return;
    acceptingRef.current = true;
    stopRingtone();
    const { session, callerNumber } = incomingCall;
    setIncomingCall(null);

    try {
      session.stateChange.addListener((state) => {
        if (state === SessionState.Established) attachRemoteAudio(session);
        if (state === SessionState.Terminated)  endCallCleanup();
      });
      await session.accept({
        sessionDescriptionHandlerOptions: { constraints: { audio: true, video: false } },
      });
      setActiveCall({ session, callerNumber, direction: 'inbound', startedAt: Date.now() });
      setCallStatus('active');
      durationRef.current = setInterval(() => setCallDuration(p => p + 1), 1000);
    } catch (err) {
      if (!err.message?.includes('Establishing')) setError('Accept failed: ' + err.message);
    } finally {
      acceptingRef.current = false;
    }
  }, [incomingCall, attachRemoteAudio, endCallCleanup]);

  // ── REJECT ───────────────────────────────────────────────────
  const rejectCall = useCallback(async () => {
    if (!incomingCall) return;
    stopRingtone();
    try { await incomingCall.session.reject(); } catch (_) {}
    setIncomingCall(null);
    setCallStatus('idle');
  }, [incomingCall]);

  // ── MAKE CALL ────────────────────────────────────────────────
  const makeCall = useCallback(async (number) => {
    if (!number?.trim() || !userAgentRef.current || regStatus !== 'registered') return false;
    setError('');

    try {
      const targetUri = UserAgent.makeURI(`sip:${number.trim()}@${SIP_DOMAIN}`);
      if (!targetUri) throw new Error('Invalid number');

      const inviter = new Inviter(userAgentRef.current, targetUri, {
        sessionDescriptionHandlerOptions: { constraints: { audio: true, video: false } },
      });

      inviter.stateChange.addListener((state) => {
        if (state === SessionState.Established) attachRemoteAudio(inviter);
        if (state === SessionState.Terminated)  endCallCleanup();
      });

      setActiveCall({ session: inviter, callerNumber: number.trim(), direction: 'outbound', startedAt: Date.now(), connecting: true });
      setCallStatus('active');
      await inviter.invite();

      setActiveCall(prev => prev ? { ...prev, connecting: false } : prev);
      durationRef.current = setInterval(() => setCallDuration(p => p + 1), 1000);

      setRecentCalls(prev =>
        [{ number: number.trim(), direction: 'outbound', at: new Date().toISOString() },
          ...prev.filter(c => c.number !== number.trim())].slice(0, 8)
      );
      return true;
    } catch (err) {
      console.error('[SIP] Call failed:', err);
      setError('Call failed: ' + err.message);
      endCallCleanup();
      return false;
    }
  }, [regStatus, attachRemoteAudio, endCallCleanup]);

  // ── RING EITHER ──────────────────────────────────────────────
  const ringEither = useCallback(async (numbers) => {
    setError('');
    for (const num of numbers) {
      setError(`Trying ${num}...`);
      const ok = await makeCall(num);
      if (ok !== false) return;
    }
    setError('No destination answered');
  }, [makeCall]);

  // ── HANGUP ───────────────────────────────────────────────────
  const hangupCall = useCallback(async () => {
    if (!activeCall) return;
    try {
      const { session } = activeCall;
      if (session.state === SessionState.Established) await session.bye();
      else if (session.state === SessionState.Establishing) {
        if (typeof session.cancel === 'function') await session.cancel();
        else await session.reject();
      }
    } catch (_) {}
    endCallCleanup();
  }, [activeCall, endCallCleanup]);

  // ── MUTE ─────────────────────────────────────────────────────
  const toggleMute = useCallback(() => {
    if (!activeCall) return;
    const pc = activeCall.session.sessionDescriptionHandler?.peerConnection;
    if (!pc) return;
    pc.getSenders().forEach(s => { if (s.track?.kind === 'audio') s.track.enabled = muted; });
    setMuted(m => !m);
  }, [activeCall, muted]);

  // ── HOLD ─────────────────────────────────────────────────────
  const toggleHold = useCallback(async () => {
    if (!activeCall) return;
    const session = activeCall.session;
    const modifier = onHold
      ? (desc) => { desc.sdp = desc.sdp.replace(/a=sendonly/g, 'a=sendrecv'); return Promise.resolve(desc); }
      : (desc) => { desc.sdp = desc.sdp.replace(/a=sendrecv/g, 'a=sendonly'); return Promise.resolve(desc); };
    try {
      await session.invite({ sessionDescriptionHandlerModifiers: [modifier] });
      setOnHold(h => !h);
    } catch (err) { console.warn('[SIP] Hold toggle failed:', err.message); }
  }, [activeCall, onHold]);

  // ── DTMF ─────────────────────────────────────────────────────
  const sendDtmf = useCallback((digit) => {
    playDtmfTone(digit);
    if (!activeCall) return;
    try {
      const sdh = activeCall.session.sessionDescriptionHandler;
      if (sdh?.sendDtmf) sdh.sendDtmf(digit);
    } catch (_) {}
  }, [activeCall]);

  // ── Cleanup only on logout/unmount, NOT on view switch ───────
  useEffect(() => {
    return () => {
      // This only fires when CallProvider itself unmounts (i.e. logout).
      // Individual view components can switch freely without touching SIP.
      unregister();
      stopRingtone();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const value = {
    // registration
    regStatus, error, register, unregister,
    // call state
    incomingCall, activeCall, callStatus,
    localStream, remoteStream,
    callDuration, muted, onHold, recentCalls,
    // actions
    acceptCall, rejectCall, makeCall, ringEither,
    hangupCall, toggleMute, toggleHold, sendDtmf,
    // transcript (shared between AgentConsole and Noc)
    liveTranscript, setLiveTranscript,
  };

  return <CallContext.Provider value={value}>{children}</CallContext.Provider>;
}
